/**
 * useCitationNavigation (Intelligence Studio)
 *
 * SRP hook: citation click -> locate file + compute highlight target + update preview state.
 * Extracted (copy/paste) from containers/IntelligenceStudioPage.jsx.
 */

export default function useCitationNavigation({
  selectedNotebook,
  setSelectedFile,
  setTargetPage,
  setTargetText,
  rightCollapsed,
  expandRight,
}) {
  // ═══════════════════════════════════════════════════════════════════════
  // ✅ WAVE 25: Enhanced citation click - navigate to source with highlighting
  // Supports: PDF pages, Excel sheets/cells, Word sections, PowerPoint slides
  // ═══════════════════════════════════════════════════════════════════════
  const handleCitationClick = citation => {
    console.log('📍 [Citation Click] Full citation data:', citation);

    if (!selectedNotebook?.files || selectedNotebook.files.length === 0) {
      console.warn('No files in notebook');
      return;
    }

    let targetFile = null;

    // Extract citation metadata with fallbacks
    const sourceIndex = citation.sourceIndex || citation.index;
    const docName =
      citation.docName ||
      citation.sourceFile?.name ||
      citation.sourceFile?.originalName ||
      citation.display?.fileName;
    const location = citation.location || citation.locationData || {};

    // 1. Try by document name first (most precise)
    if (docName && docName !== 'Document') {
      targetFile = selectedNotebook.files.find(f => {
        const fileName = f.originalName || f.name || '';
        return (
          fileName.toLowerCase() === docName.toLowerCase() ||
          fileName.toLowerCase().includes(docName.toLowerCase()) ||
          docName.toLowerCase().includes(fileName.toLowerCase())
        );
      });
      if (targetFile) {
        console.log(
          `✅ Found file by name: "${docName}" → "${targetFile.originalName || targetFile.name}"`
        );
      }
    }

    // 2. Try by sourceFile.id
    if (!targetFile && citation.sourceFile?.id) {
      targetFile = selectedNotebook.files.find(
        f => (f._id || f.id || f.fileId) === citation.sourceFile.id
      );
      if (targetFile) {
        console.log(`✅ Found file by ID: ${citation.sourceFile.id}`);
      }
    }

    // 3. Try by source index
    if (!targetFile && sourceIndex !== null && sourceIndex !== undefined) {
      if (sourceIndex >= 1 && sourceIndex <= selectedNotebook.files.length) {
        targetFile = selectedNotebook.files[sourceIndex - 1];
        console.log(
          `✅ Found file by index: Source ${sourceIndex} → "${targetFile.originalName || targetFile.name}"`
        );
      } else {
        console.warn(
          `⚠️ Source ${sourceIndex} out of range (have ${selectedNotebook.files.length} files)`
        );
      }
    }

    // 4. Try to extract filename from fullText or content
    if (!targetFile) {
      const textToSearch = citation.fullText || citation.content?.exactText || '';
      const fileNameMatch =
        textToSearch.match(/From\s*"([^"]+)"/i) ||
        textToSearch.match(/([^,\s]+\.(?:pdf|docx?|xlsx?|pptx?|txt|csv))/i);
      if (fileNameMatch) {
        const extractedName = fileNameMatch[1];
        targetFile = selectedNotebook.files.find(f =>
          (f.originalName || f.name || '').toLowerCase().includes(extractedName.toLowerCase())
        );
        if (targetFile) {
          console.log(`✅ Found file from text: "${extractedName}"`);
        }
      }
    }

    // 5. Default to first file
    if (!targetFile) {
      targetFile = selectedNotebook.files[0];
      console.log(
        `⚠️ No match, defaulting to first file: "${targetFile.originalName || targetFile.name}"`
      );
    }

    if (targetFile) {
      const fileIndex = selectedNotebook.files.indexOf(targetFile);
      const fileName = targetFile.originalName || targetFile.name || '';

      // Build highlight target based on file type
      const highlightTarget = {
        // PDF: page number and text to highlight
        page: location.pageNumber || citation.page || 1,
        // Excel: sheet name and cell coordinates
        sheetName: location.sheetName || null,
        cellCoordinates: location.cellCoordinates || null,
        rowNumber: location.rowNumber || null,
        // Word/PowerPoint: slide/section
        slideNumber: location.slideNumber || null,
        // Text to find and highlight
        highlightText:
          citation.content?.highlightText || citation.content?.exactText || citation.text || null,
        // Character offsets for precise highlighting
        startOffset: location.startOffset || null,
        endOffset: location.endOffset || null,
        // Computation info for Excel
        computation: citation.computation || null,
      };

      // Set the file with rich citation data
      setSelectedFile({
        ...targetFile,
        sourceNumber: fileIndex + 1,
        citationPage: highlightTarget.page,
        // ✅ WAVE 25: Pass full highlight target for File View
        citationHighlight: highlightTarget,
      });

      setTargetPage(highlightTarget.page);
      setTargetText(highlightTarget.highlightText);

      // ✅ WAVE 25: The citationHighlight prop on selectedFile will trigger
      // FilePreviewContent to auto-switch to File View via useEffect

      // Expand right panel if collapsed
      if (rightCollapsed) expandRight();

      console.log(`🎯 Opening file: "${fileName}"`, {
        page: highlightTarget.page,
        sheet: highlightTarget.sheetName,
        cell: highlightTarget.cellCoordinates,
        text: highlightTarget.highlightText?.slice(0, 50) + '...',
      });
    }
  };

  return { handleCitationClick };
}
